import { Title, Stack, Group, TextInput, NumberInput, Select, Button, Badge, Modal, Code } from "@mantine/core";
import { DataTable } from "mantine-datatable";
import { useQuery } from "@tanstack/react-query";
import { decisionsApi, type DecisionsFilter } from "../api/decisions.js";
import { useState } from "react";
import type { SortDecision } from "@cas/shared";

const INITIAL_FILTER: DecisionsFilter = { hours: 24, limit: 200 };

export function Decisions() {
  // Filter inputs are held in `draft` and only committed to `applied` on Refresh / Enter.
  // Free-text fields wired straight into the query key would fire a Kusto query per keystroke;
  // the workspace has no daily-quota cap yet (plan-2 backlog).
  const [draft, setDraft] = useState<DecisionsFilter>(INITIAL_FILTER);
  const [applied, setApplied] = useState<DecisionsFilter>(INITIAL_FILTER);
  const [selected, setSelected] = useState<SortDecision | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["decisions", applied],
    queryFn: () => decisionsApi.query(applied),
  });

  const apply = () => setApplied(draft);

  return (
    <Stack>
      <Title order={2}>Decisions</Title>
      <Group align="end">
        <NumberInput label="Last X hours" min={1} max={168} value={draft.hours} onChange={(v) => setDraft(f => ({ ...f, hours: Number(v) || 24 }))} />
        <TextInput label="messageId" value={draft.messageId ?? ""} onChange={(e) => setDraft(f => ({ ...f, messageId: e.currentTarget.value }))} onKeyDown={(e) => { if (e.key === "Enter") apply(); }} />
        <TextInput label="sender contains" value={draft.sender ?? ""} onChange={(e) => setDraft(f => ({ ...f, sender: e.currentTarget.value }))} onKeyDown={(e) => { if (e.key === "Enter") apply(); }} />
        <Select label="mode" data={["", "V6_LIVE", "V7_SHADOW", "V7_LIVE"]} value={draft.mode ?? ""} onChange={(v) => setDraft(f => ({ ...f, mode: v ?? undefined }))} />
        <Select label="outcome" data={["", "success", "error"]} value={draft.outcome ?? ""} onChange={(v) => setDraft(f => ({ ...f, outcome: (v || undefined) as any }))} />
        <Button onClick={apply}>Refresh</Button>
      </Group>
      <DataTable
        striped withTableBorder fetching={isLoading}
        idAccessor={(r) => `${r.messageId}-${r.version}-${r.receivedDateTime}`}
        records={data ?? []}
        onRowClick={({ record }) => setSelected(record)}
        columns={[
          { accessor: "version", title: "V", width: 50, render: (r) => <Badge color={r.version === "V7" ? "blue" : "gray"}>{r.version}</Badge> },
          { accessor: "mode", title: "Mode", width: 100 },
          { accessor: "messageId", title: "Message ID", render: (r) => <code style={{ fontSize: 11 }}>{r.messageId.slice(0, 24)}…</code> },
          { accessor: "sender", title: "Sender" },
          { accessor: "subject", title: "Subject", ellipsis: true },
          { accessor: "decidedFolderName", title: "→ Folder" },
          { accessor: "forwardedToSupport", title: "→ Support", render: (r) => r.forwardedToSupport ? "✓" : "—" },
          { accessor: "markedAsRead", title: "Read", render: (r) => r.markedAsRead ? "✓" : "—" },
          { accessor: "outcome", title: "OK", render: (r) => r.outcome === "success" ? "✅" : "❌" },
        ]}
      />
      <Modal opened={!!selected} onClose={() => setSelected(null)} title="Decision detail" size="lg">
        <Code block>{JSON.stringify(selected, null, 2)}</Code>
      </Modal>
    </Stack>
  );
}
